import React, { useRef, useEffect } from 'react';
import { Map } from './PolicyMakersMap.styles';
import { useTheme } from 'styled-components';
import L from 'leaflet';
import useSoilTypes from '../../hooks/useSoilTypes';
import useCropTypes from '../../hooks/useCropTypes';
import { t } from 'ttag';
import { getHarvestDateTypeByCode, getSeedDateTypeByCode } from '../Field/constants/filters';
import { MapFilterPanel } from '../Fields/fields.styles';
import Filter from '../Shared/Filter/Filter';
import { FaCog } from 'react-icons/fa';
import MapLayerCard from '../Fields/MapLayerCard';
import { MAP_LAYERS, MAP_LAYER_TYPES } from '../../constants/map-layers';
import { FILTERS } from '../Field/constants/filters';

const LEAFLET_PANES = {
  fields: 'fields',
};

export default function PolicyMakersMap({
  bbox,
  scope0,
  scope1,
  wasUserDrag,
  fieldsList,
  activeLayer,
  date,
  onMoveEnd,
  activeFilter,
  mapLayer,
  onFilterCloseClick,
  onOutsideFilterClick,
  onToggleFilterClick,
  onFilterChangeClick,
}) {
  const mapRef = useRef();
  const leafletMap = useRef();
  const theme = useTheme();
  const fieldsRef = useRef();

  const style_0 = {
    color: theme.fieldColor_0,
    weight: 4,
    opacity: 1,
  };

  const style_1 = {
    color: theme.fieldColor_1,
    weight: 4,
    opacity: 1,
  };

  const { getSoilTypeNameById: getSoilTypeNameById0 } = useSoilTypes(scope0);
  const { getCropNameById: getCropNameById0 } = useCropTypes(scope0);

  const { getSoilTypeNameById: getSoilTypeNameById1 } = useSoilTypes(scope1);
  const { getCropNameById: getCropNameById1 } = useCropTypes(scope1);

  useEffect(() => {
    leafletMap.current = L.map(mapRef.current, {});

    const { _southWest, _northEast } = bbox;

    const newBBox = [
      [_southWest.lat, _southWest.lng],
      [_northEast.lat, _northEast.lng],
    ];

    const newbbox = L.latLngBounds(newBBox);
    leafletMap.current.fitBounds(newbbox);
    leafletMap.current.on('zoomend', () => {
      onMoveEnd(leafletMap.current.getBounds());
    });
    leafletMap.current.on('dragend', () => {
      onMoveEnd(leafletMap.current.getBounds());
    });

    MAP_LAYERS.forEach((layer) => {
      leafletMap.current.createPane(layer.SATFARM_INTERNAL_ID);
      const pane = leafletMap.current.createPane(layer.id);
      if (layer.type === MAP_LAYER_TYPES.mapLibre) {
        L.maplibreGL({
          ...layer.settings,
          pane,
        }).addTo(leafletMap.current);
      }

      if (layer.type === MAP_LAYER_TYPES.tileLayer) {
        L.tileLayer.wms(layer.url, { ...layer.settings, pane }).addTo(leafletMap.current);
      }
    });

    return () => leafletMap.current.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!wasUserDrag) {
      const { _southWest, _northEast } = bbox;
      const latLngNorthEast = L.latLng(_northEast.lat, _northEast.lng);
      const latLngSouthWest = L.latLng(_southWest.lat, _southWest.lng);
      const newbbox = L.latLngBounds([latLngNorthEast, latLngSouthWest]);
      leafletMap.current.fitBounds(newbbox);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbox]);

  useEffect(() => {
    if (!leafletMap.current) {
      return;
    }

    leafletMap.current.eachLayer((layer) => {
      const isMapLayer =
        layer.options?.pane !== LEAFLET_PANES.fields && layer.options?.pane !== 'overlayPane';
      if (isMapLayer) {
        if (layer.options.SATFARM_INTERNAL_ID === mapLayer) {
          leafletMap.current.getPane(layer.options.pane).style.zIndex = 0;
        } else {
          leafletMap.current.getPane(layer.options.pane).style.zIndex = -1;
        }
      }
    });
  }, [mapLayer]);

  useEffect(() => {
    if (fieldsRef.current) {
      leafletMap.current.removeLayer(fieldsRef.current);
    }

    fieldsRef.current = L.layerGroup().addTo(leafletMap.current);

    fieldsList.forEach((fields, index) => {
      fields.forEach((field) => {
        L.geoJson(field.geometry, {
          style: index === 0 ? style_0 : style_1,
          satfarmId: field.id,
        })
          .bindTooltip(() => {
            const cropName = index === 0 ? getCropNameById0(field.cropId) : getCropNameById1(field.cropId);
            const soilTypeName =
              index === 0 ? getSoilTypeNameById0(field.soilTypeId) : getSoilTypeNameById1(field.soilTypeId);

            const seedDateName = getSeedDateTypeByCode(field.att_seeddate_class);
            const harvestDateName = getHarvestDateTypeByCode(field.att_harvdate_class);

            return `${t`Crop type`}: ${cropName}<br> ${t`Soil type`}: ${soilTypeName}<br><br>${t`Seed date`}: ${seedDateName}<br>${t`Harvest date`}: ${harvestDateName}`;
          })
          .addTo(fieldsRef.current);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsList, activeLayer, date]);

  return (
    <Map ref={(el) => (mapRef.current = el)}>
      <MapFilterPanel>
        <Filter
          popupDirection="right"
          shouldDisplaySaveChangesButton={false}
          onOutsideClick={onOutsideFilterClick}
          onToggleClick={onToggleFilterClick}
          onCloseClick={onFilterCloseClick}
          isOpen={activeFilter === FILTERS.mapLayer}
          label={
            <>
              {t`Map settings`}
              <FaCog className="map-settings-icon" />
            </>
          }
        >
          {MAP_LAYERS.map((layer, index) => (
            <MapLayerCard
              key={layer.settings.SATFARM_INTERNAL_ID}
              id={layer.settings.SATFARM_INTERNAL_ID}
              index={index}
              onClick={() => onFilterChangeClick(layer.settings.SATFARM_INTERNAL_ID)}
              name={layer.name}
              image={layer.image}
              checked={mapLayer === layer.settings.SATFARM_INTERNAL_ID}
            />
          ))}
        </Filter>
      </MapFilterPanel>
    </Map>
  );
}
